import {
  type ThemeOptions,
  type ComponentsProps,
  type ComponentsOverrides,
  type ComponentsVariants,
  alpha
} from '@mui/material/styles';
import type { Theme } from '@ui/ThemeRegistry/theme.types';
import { FormVariants } from './Form.types';

const defaultProps: ComponentsProps['Form'] = {};

const styleOverrides: ComponentsOverrides<Theme>['Form'] = {
  root: ({ theme, ownerState }) => ({
    'backgroundColor': `var(--mui-palette-schemes-${ownerState?.backgroundColor}-primary-main)`,

    ...(ownerState?.variant === FormVariants.default && {
      'padding': 0,

      '& [class*=Text-overline]': {
        gridRow: 2
      }
    }),

    '& [class*="RichText-root"]': {
      ...(ownerState?.variant === FormVariants.default && {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      })
    },

    '& [class*="Text-root"][class*="Grid-root"]': {
      'marginBottom': 0,
      'gridColumn': 'start/end',
      'gridTemplateRows': 'min-content',

      ...(ownerState?.variant === FormVariants.newsletter && {
        gridGap: 'var(--grid-gap-sm)',

        [theme.breakpoints.up('sm')]: {
          gridColumn: 'two-start/seven-end'
        },

        [theme.breakpoints.up('md')]: {
          gridColumn: 'three-start/ten-end'
        },

        [theme.breakpoints.up('lg')]: {
          gridColumn: 'four-start/nine-end'
        }
      }),

      ...(ownerState?.variant === FormVariants.default && {
        'paddingTop': 'var(--section-padding)',
        'paddingBottom': 0,
        'gridGap': 'var(--grid-gap-xs)',
        'height': 'auto',

        '[class*=Form-introText]>*:last-child': {
          paddingBottom: 'var(--section-padding)'
          // marginTop: 'auto'
        },

        '[class*=Form-introText]>img:last-child': {
          paddingBottom: 0,
          marginTop: 'auto'
        },

        [theme.breakpoints.up('md')]: {
          gridColumn: 'start / half',
          gridRow: 1
          // height: 'fit-content'
        }
      }),

      '& h3': {
        ...theme.typography.h3,

        ...(ownerState?.variant === FormVariants.default && {
          marginBottom: 'var(--grid-gap-sm)'
        })
      }
    },

    '& [class*=Form-mainContentWrap]': {
      ...(ownerState?.variant === FormVariants.default && {
        paddingTop: 'var(--section-padding)',
        paddingBottom: 'var(--section-padding)',
        gridRow: 2,
        gridColumn: 'start / end',

        [theme.breakpoints.up('md')]: {
          gridRow: 1,
          height: 'fit-content',

          ...(!!ownerState?.introText && {
            gridColumn: 'half / end'
          })
        }
      }),

      ...(ownerState?.variant === FormVariants.newsletter && {
        ':is(body, &)': {
          '& .hbspt-form': {
            [`& form#hsForm_${ownerState?.hubspotFormId}`]: {
              '& .hs-submit': {
                alignSelf: 'center'
              }
            }
          }
        },

        [theme.breakpoints.up('sm')]: {
          gridColumn: 'two-start/seven-end'
        },

        [theme.breakpoints.up('md')]: {
          gridColumn: 'three-start/ten-end'
        },

        [theme.breakpoints.up('lg')]: {
          gridColumn: 'four-start/nine-end'
        }
      })
    },

    '[data-hs-forms-root]': {
      '& ': {
        //}.hbspt-form': {
        'containerType': 'inline-size',
        'position': 'relative',
        'width': '100%',
        'display': 'flex',
        'flexDirection': 'column',

        '& form': {
          'display': 'flex',
          'flexDirection': 'column',
          'gap': theme.spacing(1),
          'backgroundColor': `var(--mui-palette-schemes-${ownerState?.backgroundColor}-primary-main)`,
          'marginBottom': 0,

          '& .hs-recaptcha': {
            display: 'none'
          },

          '& .hs-form__virality-link': {
            display: 'none'
          },

          '& .hs_error_rollup': {
            display: 'none'
          },

          '& fieldset': {
            maxWidth: '100%',
            display: 'flex',
            margin: 0,
            border: 'none',
            padding: 'unset',
            gap: 'var(--grid-gap)'
          },

          '& .field.hs-form-field': {
            'marginRight': 0,
            'position': 'relative',
            'width': '100%',
            'float': 'none',
            'background': 'transparent',
            'display': 'grid',

            '& label': {
              margin: theme.spacing(1, 0)
            },

            '& .hs-error-msg': {
              margin: 0
            },

            '& span': {
              ...theme.typography.body1
            },

            '& legend': {
              ...theme.typography.body2,
              gridRow: 3,
              margin: theme.spacing(0.5, 2)
            },

            '& .input': {
              'marginRight': 0,

              '& .hs-input': {
                ...theme.typography.body1,
                'width': '100%',
                'maxWidth': '100%',
                'padding': theme.spacing(2, 3),
                'border': 'none',

                '&:hover': {},

                '&:focus': {},

                '&:focus-visible': {}
              },

              'input': {
                '&': {
                  ...theme.typography.body1,
                  'height': 'auto',

                  '&::placeholder': {
                    opacity: 0.5
                  }
                }
              },

              'textarea': {
                ...theme.typography.body1,
                'height': 300,
                'maxHeight': '80vh',

                '&::placeholder': {
                  opacity: 0.5
                }
              },

              'select': {
                '&': {
                  ...theme.typography.body1,

                  'appearance': 'none',
                  'backgroundImage':
                    'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTciIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxNyAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4gXAogICAgICAgICAgICAgICAgICAgICA8cGF0aCBkPSJNMS45OTc1IDAuNzM3NTQ5TDguNSA2LjQ2MjU1TDE1LjAwMjUgMC43Mzc1NDlMMTcgMi41MDAwNUw4LjUgMTBMMCAyLjUwMDA1TDEuOTk3NSAwLjczNzU0OVoiIGZpbGw9IiNBNEE0QTQiLz4gXAogICAgICAgICAgICAgICAgICAgIDwvc3ZnPg==)',
                  'backgroundPosition': 'right center',
                  'backgroundRepeat': 'no-repeat',
                  'backgroundSize': 17,
                  'backgroundOrigin': 'content-box',

                  '&.is-placeholder': {
                    color: alpha(theme.palette.common.black, 0.5)
                  }
                }
              },

              '&.error': {
                borderColor: theme.palette.error.main,
                borderWidth: 1
              }
            }
          },

          '& .hs-fieldtype-checkbox.field.hs-form-field': {
            'ul': {
              listStyleType: 'none',
              padding: 0
            },

            '& .hs-form-checkbox': {
              width: 'fit-content',
              marginBottom: 'var(--grid-gap)'
            },

            '& .hs-form-checkbox-display': {
              'display': 'flex',
              'alignItems': 'center',
              'cursor': 'pointer',

              '& .hs-input': {
                visibility: 'hidden',
                width: 20,
                height: 20,
                padding: 0,
                margin: 0
              },

              '& span': {
                ...theme.typography.body1,
                color: theme.palette.common.black,
                paddingLeft: 'var(--grid-gap)'
              },

              "& input[type='checkbox']::before": {
                width: 20,
                height: 20,
                position: 'absolute',
                cursor: 'pointer',
                backgroundImage:
                  'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjEuNSIgc3Ryb2tlPSIjQkVCRUJFIi8+Cjwvc3ZnPgo=)',
                content: '""',
                visibility: 'visible'
              },

              "& input[type='checkbox']:checked::before": {
                backgroundImage:
                  'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjEuNSIgZmlsbD0iIzA1ODBFOCIvPgo8cmVjdCB4PSIwLjUiIHk9IjAuNSIgd2lkdGg9IjE5IiBoZWlnaHQ9IjE5IiByeD0iMS41IiBzdHJva2U9IiNCRUJFQkUiLz4KPHBhdGggZD0iTTE2Ljk1NTEgNi40NzYxMkwxNS4zODc4IDVMNy45MTI3NCAxMi4wNDc2TDQuNTY3MjcgOC44OTMzMkwzIDEwLjM2OTRMNy45MTI3NyAxNUwxNi45NTUxIDYuNDc2MTJaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K)'
              }
            }
          },

          '& .actions': {
            '& input': {
              ...theme.typography.body1,
              'backgroundColor': 'var(--mui-palette-schemes-navyBlue-primary-main)',
              'color': 'var(--mui-palette-schemes-navyBlue-primary-contrastText)',
              'padding': theme.spacing(1.5, 3.75),
              'borderRadius': theme.spacing(10),
              'textTransform': 'uppercase',
              'marginTop': theme.spacing(4),
              'cursor': 'pointer',
              'border': 'none',

              '&:hover': {
                backgroundColor: 'var(--mui-palette-schemes-navyBlue-primary-light)',
                color: 'var(--mui-palette-schemes-navyBlue-primary-main)'
              },

              '&:active': {
                backgroundColor: 'var(--mui-palette-schemes-navyBlue-primary-lighter)',
                color: 'var(--mui-palette-schemes-navyBlue-primary-main)'
              }
            }
          },

          '& .hs-error-msgs': {
            'margin': theme.spacing(0.5, 2),
            'padding': 0,
            'listStyle': 'none',

            '& li label': {
              ...theme.typography.body2,
              display: 'block !important',
              color: theme.palette.error.main,
              textAlign: 'left'
            }
          }
        }
      }
    }
  }),

  contentOuterGrid: ({ theme, ownerState }) => ({
    'margin': 0,
    'rowGap': 'var(--grid-gap)',

    [theme.containerBreakpoints.up('sm')]: {
      margin: 'var(--grid-margin-lg) 0'
    },

    '> *': {
      gridColumnStart: 'auto'
    }
  })
};

const createVariants = (_theme: Theme): ComponentsVariants['Form'] => [];

export const formTheme = (theme: Theme): ThemeOptions => ({
  components: {
    Form: {
      defaultProps,
      styleOverrides,
      variants: createVariants(theme)
    }
  }
});

export default formTheme;
